import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Bot, Copy, Check, Power, Unplug, RefreshCw } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useAuth } from '../../context/AuthContext';
import { TOOL_LABEL, STATUS_META } from '../../lib/agentActions';

// Admin → Assistant: the connector's switch, how to connect an assistant
// (Muse, Claude), which ones you have connected, and the proposals they made.

const MUSE_PROMPT = (url) => `Build a custom integration to the NexSav CRM. Its MCP server URL is ${url}. Sign in with my NexSav staff login when asked. I want you to look up schools, summarise them, and propose logging interactions, moving pipeline stages, setting meeting dates, creating tasks, drafting invoices and adding contacts. Never claim something was done until action_status says executed.`;

function CopyButton({ text, label = 'Copy' }) {
  const [done, setDone] = useState(false);
  return (
    <button type="button" onClick={async () => { try { await navigator.clipboard.writeText(text); setDone(true); setTimeout(() => setDone(false), 1500); } catch { /* ignore */ } }} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11.5px] text-gray-300 border border-white/[0.1] hover:text-white">
      {done ? <Check className="w-3 h-3" aria-hidden="true" /> : <Copy className="w-3 h-3" aria-hidden="true" />} {done ? 'Copied' : label}
    </button>
  );
}

export default function Assistant() {
  const { user } = useAuth();
  const [status, setStatus] = useState(null);
  const [pending, setPending] = useState([]);
  const [history, setHistory] = useState([]);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try {
      const [s, p, h] = await Promise.all([
        api.get('/api/admin/agent-connector'),
        api.get('/api/admin/agent-actions?status=pending'),
        api.get('/api/admin/agent-actions?status=history&limit=30'),
      ]);
      setStatus(s); setPending(p?.actions || []); setHistory(h?.actions || []);
    } catch (err) { setError(err?.message || 'Could not load the assistant settings'); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const toggle = async () => {
    if (busy || !status?.canToggle) return;
    const next = !status.enabled;
    if (!window.confirm(next ? 'Turn the assistant connector on? Staff can then connect Muse or Claude to the CRM. Every write still needs approval here.' : 'Turn the assistant connector off? Connected assistants stop working immediately.')) return;
    setBusy(true);
    try { await api.put('/api/admin/settings/agent_connector_enabled', { value: next }); await load(); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Could not change the switch'); }
    finally { setBusy(false); }
  };

  const revoke = async (g) => {
    if (busy) return;
    if (!window.confirm(`Disconnect ${g.clientName || 'this assistant'}? It will have to be connected again.`)) return;
    setBusy(true);
    try { await api.post('/api/admin/agent-connector/revoke', { grantId: g.id }); await load(); }
    catch (err) { setError(err instanceof ApiError ? err.message : 'Could not disconnect'); }
    finally { setBusy(false); }
  };

  const Row = ({ a }) => {
    const st = STATUS_META[a.status] || STATUS_META.pending;
    return (
      <li className="py-2.5 flex items-start gap-3" data-testid={`proposal-${a.id}`}>
        <span className={`mt-0.5 inline-flex items-center px-2 py-0.5 rounded-full text-[10px] border shrink-0 ${st.tone}`}>{st.label}</span>
        <div className="min-w-0 flex-1">
          <Link to={`/admin/assistant/actions/${a.id}`} className="text-[13px] text-white hover:text-[#B0FC38]">{a.summary}</Link>
          <div className="text-[11px] text-gray-500 mt-0.5">{TOOL_LABEL[a.tool] || a.tool} · {a.clientName || 'assistant'}{a.userName && a.userId !== user?.id ? ` · asked by ${a.userName}` : ''} · {String(a.createdAt).slice(0, 16)}</div>
        </div>
      </li>
    );
  };

  return (
    <div className="max-w-4xl mx-auto">
      <div className="mb-5 flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h1 className="text-xl md:text-2xl font-semibold text-white tracking-tight"><span className="serif-acc accent-text">Assistant</span></h1>
          <p className="text-[13px] text-gray-500 mt-0.5">Talk to Muse or Claude about schools while you’re out. They can read the CRM and propose changes; nothing changes until you approve it here.</p>
        </div>
        <button type="button" onClick={load} className="inline-flex items-center gap-1.5 px-3 py-2 rounded-full text-[12px] text-gray-300 border border-white/[0.1] hover:text-white"><RefreshCw className="w-3.5 h-3.5" aria-hidden="true" /> Refresh</button>
      </div>
      {error && <div role="alert" className="mb-4 text-[12px] text-red-300 bg-red-500/10 border border-red-500/20 rounded-lg p-3">{error}</div>}

      {status && (
        <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 mb-4" data-testid="connector-status">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <Power className={`w-4 h-4 ${status.enabled ? 'text-[#B0FC38]' : 'text-gray-600'}`} aria-hidden="true" />
              <span className="text-[14px] text-white font-medium">Connector is {status.enabled ? 'on' : 'off'}</span>
            </div>
            {status.canToggle ? (
              <button type="button" onClick={toggle} disabled={busy} className={`px-3.5 py-1.5 rounded-full text-[12px] font-medium disabled:opacity-50 ${status.enabled ? 'border border-white/[0.12] text-gray-300 hover:text-red-300' : 'bg-[#B0FC38] text-gray-900 hover:bg-[#9BE22E]'}`} data-testid="connector-toggle">{status.enabled ? 'Turn off' : 'Turn on'}</button>
            ) : <span className="text-[11.5px] text-gray-500">Only an admin can turn it on or off.</span>}
          </div>
          <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4 text-[12.5px]">
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Connect Meta Muse</div>
              <p className="text-gray-400 mb-2">Send Muse this message. It signs you in through the portal and asks for consent once.</p>
              <pre className="whitespace-pre-wrap break-words text-[11.5px] text-gray-300 bg-black/40 border border-white/[0.06] rounded-lg p-3" data-testid="muse-prompt">{MUSE_PROMPT(status.mcpUrl)}</pre>
              <div className="mt-2"><CopyButton text={MUSE_PROMPT(status.mcpUrl)} label="Copy the message" /></div>
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Connect Claude</div>
              <p className="text-gray-400 mb-2">In claude.ai go to Settings → Connectors → Add custom connector and paste the server URL. It then works in the Claude mobile app and voice mode too.</p>
              <div className="flex items-center gap-2 flex-wrap"><code className="text-[12px] text-white bg-black/40 border border-white/[0.06] rounded-lg px-2.5 py-1.5" data-testid="mcp-url">{status.mcpUrl}</code><CopyButton text={status.mcpUrl} label="Copy URL" /></div>
              <p className="text-[11.5px] text-gray-500 mt-3">In Muse, turn off “use my conversations for training” in its settings: school and principal names will come up.</p>
            </div>
          </div>
          <div className="mt-4 pt-3 border-t border-white/[0.05]">
            <div className="text-[11px] uppercase tracking-wider text-gray-500 mb-1.5">Your connected assistants</div>
            {status.grants?.length ? (
              <ul className="divide-y divide-white/[0.05] list-none m-0 p-0">
                {status.grants.map((g) => (
                  <li key={g.id} className="py-2 flex items-center justify-between gap-3" data-testid={`grant-${g.id}`}>
                    <div className="text-[12.5px] text-gray-200">{g.clientName || g.clientId}<span className="text-gray-500"> · connected {g.createdAt ? String(g.createdAt).slice(0, 10) : ''}</span></div>
                    <button type="button" onClick={() => revoke(g)} disabled={busy} className="inline-flex items-center gap-1 px-2.5 py-1 rounded-lg text-[11.5px] text-gray-400 border border-white/[0.1] hover:text-red-300 disabled:opacity-50" data-testid={`revoke-${g.id}`}><Unplug className="w-3 h-3" aria-hidden="true" /> Disconnect</button>
                  </li>
                ))}
              </ul>
            ) : <p className="text-[12px] text-gray-500">None yet. Connect one with the steps above.</p>}
          </div>
        </section>
      )}

      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4 mb-4" data-testid="pending-proposals">
        <h2 className="text-[13px] font-medium text-white inline-flex items-center gap-1.5"><Bot className="w-3.5 h-3.5 text-gray-400" aria-hidden="true" /> Waiting for your decision</h2>
        {pending.length === 0 ? <p className="text-[12px] text-gray-500 mt-2">Nothing pending.</p> : <ul className="divide-y divide-white/[0.05] list-none m-0 p-0 mt-1">{pending.map((a) => <Row key={a.id} a={a} />)}</ul>}
      </section>

      <section className="rounded-2xl border border-white/[0.06] bg-white/[0.02] p-4" data-testid="proposal-history">
        <h2 className="text-[13px] font-medium text-white">Recent proposals</h2>
        {history.length === 0 ? <p className="text-[12px] text-gray-500 mt-2">No history yet.</p> : <ul className="divide-y divide-white/[0.05] list-none m-0 p-0 mt-1">{history.map((a) => <Row key={a.id} a={a} />)}</ul>}
      </section>
    </div>
  );
}
